import { useCallback, useRef } from 'react'
import useWebSocket from 'react-use-websocket'

import {
  ChatError,
  ChatErrorType,
  ChatResponse,
  ChatHistoryResponse,
  ChatDefaultsResponse,
  ChatQueryResponse,
  ChatExplainResponse,
  ChatUsersResponse,
  ChatResponseErrorItem,
  ChatRequest,
} from '@dc-shared-web/providers/ChatProvider/types'

const formatError = (error: ChatResponseErrorItem): ChatError => {
  switch (error.error_id) {
    case 'missing_text':
      return {
        type: ChatErrorType.MissingText,
        message: error.error_message,
      }
    case 'missing_doc_kira_id':
      return {
        type: ChatErrorType.MissingDocKiraId,
        message: error.error_message,
      }
    case 'missing_api_token':
      return {
        type: ChatErrorType.MissingAPIToken,
        message: error.error_message,
      }
    case 'missing_user_id':
      return {
        type: ChatErrorType.MissingUserId,
        message: error.error_message,
      }
    case 'missing_doc_guid':
      return {
        type: ChatErrorType.MissingDocGUID,
        message: error.error_message,
      }
    case 'missing_convo_settings':
      return {
        type: ChatErrorType.MissingConvoSettings,
        message: error.error_message,
      }
    case 'document_not_indexed':
      return {
        type: ChatErrorType.DocumentNotIndexed,
        message: error.error_message,
      }
    case 'doc_id_not_found':
      return {
        type: ChatErrorType.DocIdNotFound,
        message: error.error_message,
      }
    case 'query_max_retries_exceeded':
      return {
        type: ChatErrorType.QueryMaxRetriesExceeded,
        message: error.error_message,
      }
    case 'unknown_error':
      return {
        type: ChatErrorType.UnknownError,
        message: error.error_message,
      }
    default:
      return {
        type: ChatErrorType.Default,
        message: error.error_message,
      }
  }
}

const useChatWebSocket = (
  url: string,
  handlers: {
    onHistory: (data: ChatHistoryResponse) => void
    onDefaults: (data: ChatDefaultsResponse) => void
    onQuery: (data: ChatQueryResponse) => void
    onExplain: (data: ChatExplainResponse) => void
    onChatUsers: (data: ChatUsersResponse) => void
    onErrors: (errors: ChatError[], data?: ChatResponse) => void
  }
) => {
  const connected = useRef(false)

  const onOpen = useCallback(() => {
    connected.current = true
  }, [])

  const onMessage = useCallback(
    (data: ChatResponse) => {
      if (data.errors && data.errors.length > 0) {
        handlers.onErrors(data.errors.map(formatError), data)
        return
      }

      if (data?.payload) {
        switch (data.command) {
          case 'history':
            handlers.onHistory(data as ChatHistoryResponse)
            break
          case 'defaults':
            handlers.onDefaults(data as ChatDefaultsResponse)
            break
          case 'query':
            handlers.onQuery(data as ChatQueryResponse)
            break
          case 'explain':
            handlers.onExplain(data as ChatExplainResponse)
            break
          case 'chat_users':
            handlers.onChatUsers(data as ChatUsersResponse)
            break
          default:
            handlers.onErrors(
              [
                {
                  type: ChatErrorType.UnknownCommand,
                  message: `Unknown command ${data.command} response`,
                },
              ],
              data
            )
            break
        }
      }
    },
    [handlers]
  )

  const onError = useCallback(
    (e: Event) => {
      handlers.onErrors([
        {
          type: ChatErrorType.Default,
          message: `WebSocket error: ${e}`,
        },
      ])
    },
    [handlers]
  )

  const onClose = useCallback(() => {
    connected.current = false
  }, [])

  const { sendJsonMessage, readyState } = useWebSocket(url, {
    onOpen,
    onMessage: (e: { data?: string }) => {
      if (e.data) {
        onMessage(JSON.parse(e.data))
      }
    },
    onError,
    onClose,
    shouldReconnect: () => true,
  })

  const sendRequest = useCallback(
    (request: ChatRequest) => {
      if (connected.current) {
        try {
          sendJsonMessage(request)
        } catch (e) {
          handlers.onErrors([
            {
              type: ChatErrorType.SendingError,
              message: `Error sending request: ${(e as any)?.message}`,
            },
          ])
        }
      } else {
        handlers.onErrors([
          {
            type: ChatErrorType.SendingError,
            message: 'WebSocket is not connected. Cannot send request.',
          },
        ])
      }
    },
    [sendJsonMessage, handlers]
  )

  return {
    readyState,
    sendRequest,
  }
}

export default useChatWebSocket
